import React, { FC, useEffect, useLayoutEffect, useState } from "react"
import Loading from "../../../ui/Loading"
import { KButton, KSpan, KInput, KDropdown } from "kahuna-base-react-components"
import { KSelectOption } from "kahuna-base-react-components/dist/components/KDropdown/KDropdown"
import { users } from "../../../../apis"
import { UserLog } from "../../../../types"
import { mapListToCamelCase } from "../../../../utility"

import { connect } from "react-redux"
import { RootState } from "../../../../store"
import { SetURLSearchParams } from "react-router-dom"
import SearchUserComponent from "../../../ui/components/SearchUserComponent"

const columnWidths: any = {
  ID: "5%",
  TYPE_AND_CREATED: "20.5%",
  RELEASE_OR_MODEL: "19.5%",
  USER: "10%",
  LOG: "45%"
}

type AdminUserLogProps = {
  params: URLSearchParams
  setParams: SetURLSearchParams
  headerDiv: JSX.Element
}

const ExpandableLog = ({ children }: { children: React.ReactNode }) => {
  const [open, setOpen] = useState(false)

  return (
    <div
      onClick={() => setOpen(!open)}
      style={{
        position: "relative",
        cursor: "pointer",
        maxHeight: open ? "none" : "6em", // ~4 lines
        minHeight: "3em",
        overflow: "hidden"
      }}
    >
      {children}

      {/* fade overlay when collapsed */}
      {!open && (
        <div
          style={{
            position: "absolute",
            bottom: 0,
            left: 0,
            right: 0,
            height: "2.5em",
            background: "linear-gradient(to bottom, rgba(255,255,255,0), rgba(255,255,255,1))",
            pointerEvents: "none"
          }}
        />
      )}
    </div>
  )
}

const isPrimitive = (v: any) => v === null || ["string", "number", "boolean"].includes(typeof v)

const formatValue = (value: any, indent = 0): string => {
  const pad = " ".repeat(indent)

  // primitive
  if (isPrimitive(value)) {
    if (typeof value === "string") return `"${value}"`
    return String(value)
  }

  // array
  if (Array.isArray(value)) {
    const allPrimitive = value.every(isPrimitive)

    // 👉 INLINE primitive arrays
    if (allPrimitive) {
      return `[${value.map((v) => (typeof v === "string" ? `"${v}"` : String(v))).join(", ")}]`
    }

    // 👉 nested arrays / objects pretty
    const items = value
      .map((v) => formatValue(v, indent + 2))
      .map((v) => `${" ".repeat(indent + 2)}${v}`)
      .join(",\n")

    return `[\n${items}\n${pad}]`
  }

  // object
  if (typeof value === "object") {
    const entries = Object.entries(value).map(([k, v]) => {
      return `${" ".repeat(indent + 2)}${k}: ${formatValue(v, indent + 2)}`
    })

    return `{\n${entries.join(",\n")}\n${pad}}`
  }

  return String(value)
}

const PrettyJson = ({ jsonString }: { jsonString: string }) => {
  try {
    const data = JSON.parse(jsonString)
    const formatted = formatValue(data)
    const structuredData = formatted
      .replace(/^\{\n/, "") // remove opening `{` + newline
      .replace(/\n\}$/, "") // remove closing newline + `}`
      .replace(/^  /gm, "") // remove one level of indentation
    return (
      <div style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
        <KSpan text={structuredData} />
      </div>
    )
  } catch (e) {
    return <KSpan text={jsonString} />
  }
}

const AdminUserLog: FC<AdminUserLogProps> = (props) => {
  const [loading, setLoading] = useState(false)
  const [selectedActionUser, setSelectedActionUser] = useState<KSelectOption | undefined>(
    props.params.has("log_user_id") && props.params.get("log_user_id")
      ? { label: props.params.get("log_user_id") || "", value: parseInt(props.params.get("log_user_id") || "0") }
      : undefined
  )
  const [selectedAuditUser, setSelectedAuditUser] = useState<KSelectOption | undefined>(
    props.params.has("audit_user_id") && props.params.get("audit_user_id")
      ? { label: props.params.get("audit_user_id") || "", value: parseInt(props.params.get("audit_user_id") || "0") }
      : undefined
  )
  const [upc, setUpc] = useState<string>(props.params.get("log_upc") || "")
  const [entityId, setEntityId] = useState<string>("")
  const [offset, setOffset] = useState(0)
  const [limit, setLimit] = useState(10)
  const [userLogList, setUserLogList] = useState<UserLog[]>([])
  const [hasNext, setHasNext] = useState(false)
  const [typeOptions, setTypeOptions] = useState<KSelectOption[]>([])
  const [modelOptions, setModelOptions] = useState<KSelectOption[]>([])
  const [selectedModel, setSelectedModel] = useState<KSelectOption[]>([])
  const [selectedType, setSelectedType] = useState<KSelectOption[]>([])

  const scrollPosRef = React.useRef(0)

  useLayoutEffect(() => {
    if (!loading && scrollPosRef.current > 0) {
      window.scrollTo({
        top: scrollPosRef.current,
        behavior: "auto"
      })
    }
  }, [userLogList])

  const fetchUserLogOptions = () => {
    users(`/user_log_options/`, "GET").then((resp) => {
      if (!resp.data.result) {
        return
      }
      setTypeOptions(resp.data.result.type_options.map((option: string) => ({ label: option, value: option })))
      setModelOptions(resp.data.result.model_options.map((option: string) => ({ label: option, value: option })))
    })
  }

  const fetchUserLogList = (offset: number) => {
    const userIdParam = selectedActionUser ? `&user_id=${selectedActionUser.value}` : ""
    const auditIdParam = selectedAuditUser ? `&entity_id=${selectedAuditUser.value}&model=User` : ""
    const modelParam = selectedModel ? `&model=${selectedModel.map((option) => option.value).join(",")}` : ""
    const typeParam = selectedType ? `&type=${selectedType.map((option) => option.value).join(",")}` : ""
    const entityIdParam = entityId ? `&entity_id=${entityId}` : ""
    const releaseUpcParam = upc ? `&upc=${upc}` : ""
    const idParam = props.params.has("log_id") ? `&id=${props.params.get("log_id")}` : ""

    setLoading(true)
    users(
      `/user_log/?offset=${offset}&limit=${limit}${userIdParam}${entityIdParam}${releaseUpcParam}${idParam}${modelParam}${typeParam}${auditIdParam}`,
      "GET"
    ).then((resp) => {
      if (offset === 0) {
        setUserLogList(mapListToCamelCase(resp.data.results))
      } else {
        setUserLogList([...userLogList, ...mapListToCamelCase(resp.data.results)])
      }
      setHasNext(resp.data.results.length === limit)
      setLoading(false)
    })
  }

  useEffect(() => {
    fetchUserLogOptions()
    fetchUserLogList(0)
  }, [])

  const renderUserLogs = () => {
    return (
      <div className="pt-4 pb-4" style={{ display: "flex", flexDirection: "column" }}>
        <div className="flex pb-2 gap-2" style={{ width: "100%" }}>
          {Object.keys(columnWidths).map((header) => {
            return (
              <div key={`header_${header}`} style={{ width: columnWidths[header] }}>
                <KSpan fontWeight={500} text={header.replaceAll("_", " ")} />
              </div>
            )
          })}
        </div>
        {userLogList.map((userLog) => {
          return (
            <div
              key={`user_log_${userLog.id}`}
              data-testid="admin-user-log-row"
              style={{ width: "100%" }}
              className="pt-5"
            >
              <div className="flex pt-2 gap-2 items-center" style={{ width: "100%", borderTop: "1px dashed grey" }}>
                <div style={{ width: columnWidths.ID }} className="flex gap-1 items-center">
                  <KSpan text={userLog.id?.toString() || "-"} />
                </div>
                <div style={{ width: columnWidths.TYPE_AND_CREATED }} className="flex flex-col gap-1">
                  <KSpan text={userLog.type} fontWeight={500} color="#111" />
                  <KSpan text={userLog.created?.substring(0, 19)?.replace("T", " ") || "-"} />
                </div>
                <div style={{ width: columnWidths.RELEASE_OR_MODEL }} className="flex gap-1 items-center">
                  {userLog.release?.title ? (
                    <div className="flex flex-col gap-1">
                      <KSpan text={userLog.release?.title || "-"} />
                      <KSpan text={userLog.release?.upc || "-"} />
                    </div>
                  ) : (
                    <div className="flex flex-col gap-1" style={{ overflow: "hidden" }}>
                      <KSpan text={userLog.model || "-"} />
                      <KSpan text={userLog.entityId?.toString() || "-"} />
                    </div>
                  )}
                </div>
                <div style={{ width: columnWidths.USER }} className="flex gap-1 items-center">
                  <KSpan text={`${userLog.user?.firstName || "-"} ${userLog.user?.lastName || "-"}`} />
                </div>
                <div style={{ width: columnWidths.LOG }} className="flex flex-col gap-1">
                  <ExpandableLog>
                    {userLog.log?.includes('"body"') ||
                    userLog.log?.includes('"query_params"') ||
                    userLog.log?.includes('"kwargs"') ? (
                      <PrettyJson jsonString={userLog.log} />
                    ) : (
                      userLog.log?.split("\n").map((line, index) => (
                        <div key={`log_line_${index}`}>
                          <KSpan text={line} />
                        </div>
                      ))
                    )}
                  </ExpandableLog>
                </div>
              </div>
            </div>
          )
        })}
      </div>
    )
  }

  const applyFilters = () => {
    if (props.params.has("log_id")) {
      props.params.delete("log_id")
    }
    setOffset(0)
    fetchUserLogList(0)
  }

  return (
    <div className="catalog flex flex-col bg-[#fff] px-8 pt-4">
      {loading && <Loading />}
      <div className="flex justify-between items-center mb-6">
        {props.headerDiv}
        <div className="flex gap-4">
          <div className="flex flex-col gap-1">
            <KSpan text="UPC" />
            <div>
              <KInput value={upc} onChange={(value) => setUpc(value)} placeholder="Release UPC" width={105} />
            </div>
          </div>
          <div className="flex flex-col gap-1">
            <KSpan text="Model" />
            <div>
              <KDropdown
                isClearable
                width={140}
                isMulti
                closeMenuOnSelect={false}
                menuWidth={170}
                selected={selectedModel}
                options={modelOptions}
                onSelect={(selected) => setSelectedModel(selected as KSelectOption[])}
                placeholder="Select model"
              />
            </div>
          </div>
          <div className="flex flex-col gap-1">
            <KSpan text="ID" />
            <div>
              <KInput value={entityId} onChange={(value) => setEntityId(value)} placeholder="Entity ID" width={50} />
            </div>
          </div>
          <div className="flex flex-col gap-1">
            <KSpan text="Type" />
            <div>
              <KDropdown
                isClearable
                isMulti
                closeMenuOnSelect={false}
                placeholder="Select type"
                width={320}
                selected={selectedType}
                menuWidth={350}
                options={typeOptions}
                onSelect={(selected) => setSelectedType(selected as KSelectOption[])}
              />
            </div>
          </div>
          <div className="flex flex-col gap-1">
            <KSpan text="Action User" />
            <SearchUserComponent
              className="relative"
              selectedUser={selectedActionUser}
              setSelectedUser={setSelectedActionUser}
            />
          </div>
          <div className="flex flex-col gap-1">
            <KSpan text="Audit User" />
            <SearchUserComponent
              className="relative"
              selectedUser={selectedAuditUser}
              setSelectedUser={setSelectedAuditUser}
            />
          </div>
          <div className="mt-6">
            <KButton
              height="35px"
              text="Apply Filters"
              background="black"
              textColor="white"
              onClick={() => applyFilters()}
            />
          </div>
        </div>
      </div>
      {renderUserLogs()}
      {hasNext && (
        <div className="flex justify-center pb-6 ">
          <KButton
            width="150px"
            background="transparent"
            onClick={() => {
              scrollPosRef.current = window.scrollY
              setOffset(offset + limit)
              fetchUserLogList(offset + limit)
            }}
            text={"Load More"}
          />
        </div>
      )}
    </div>
  )
}

const mapStateToProps = (state: RootState) => {
  return {
    user: state.auth.user
  }
}

export default connect(mapStateToProps, {})(AdminUserLog)
